import * as React from "react";
import * as React44ms from "../../Util/form-utils/react-44ms";

import {
  ACountry,
  CheckboxInput2023,
  CheckboxInput2023Expects,
  CheckboxInput2023Props,
  ProminentErrorMessageBox,
  useCustomerInformationQuery,
} from "../../Common";

import { SavedAddressExpects } from "../model";

import {
  DeliveryDetailsUserExperience_ProminentErrorAreas,
  useDeliveryDetailsUserExperience_asController,
} from "../providers";

import { ReactCheckout2022_AddressFormTypes } from "./delivery-details-address-form-types";
import {
  SavedAddressItem,
  SavedAddressItemProps,
} from "./delivery-details-saved-address-item";

export interface SavedAddressFormFragmentProps {
  addressFormType: ReactCheckout2022_AddressFormTypes;
  handleSelectAddress: SavedAddressItemProps["handleSelectAddress"];
  handleEnterANewAddressInstead: React.MouseEventHandler<HTMLAnchorElement>;
  formRef: React44ms.Api.TypedRef<SavedAddressExpects>;
  formRef_isBillingAndShippingTheSame?: React44ms.Api.TypedRef<CheckboxInput2023Expects>;
  shouldRestrictToCountry?: ACountry | undefined;
}
export const SavedAddressFormFragment: React.FunctionComponent<
  SavedAddressFormFragmentProps
> = (props) => {
  const [formState, locate] = React44ms.React.useForm(props.formRef);

  const { data: customerInformation } = useCustomerInformationQuery();

  const {
    handleLastSelected_isBillingAndShippingTheSameChange:
      deliveryDetailsUserExperience_asController_handleLastSelected_isBillingAndShippingTheSameChange,
  } = useDeliveryDetailsUserExperience_asController();

  const currentlySelectedAddressId = React.useMemo(() => {
    return formState?.value;
  }, [formState, formState?.value]);

  const handleSelectAddress: SavedAddressItemProps["handleSelectAddress"] =
    React.useCallback(
      (address) => {
        React44ms.React.Event.dispatch(
          React44ms.React.Convenient.input.onChange,
          locate,
          address.id
        );

        props.handleSelectAddress(address);
      },
      [props.addressFormType, locate, props.handleSelectAddress]
    );

  const handleLastSelected_isBillingAndShippingTheSameChange: CheckboxInput2023Props["onChange"] =
    React.useCallback(
      (formRef, newValue) => {
        React44ms.React.Event.dispatch(
          React44ms.React.Convenient.input.onChange,
          formRef,
          newValue
        );

        deliveryDetailsUserExperience_asController_handleLastSelected_isBillingAndShippingTheSameChange(
          newValue
        );
      },
      [
        deliveryDetailsUserExperience_asController_handleLastSelected_isBillingAndShippingTheSameChange,
      ]
    );

  const addressesAvailableToSelect = React.useMemo(() => {
    return (customerInformation?.addresses || []).filter(
      (anyAddress) =>
        !props.shouldRestrictToCountry ||
        anyAddress.country_code?.toLowerCase() === props.shouldRestrictToCountry
    );
  }, [customerInformation?.addresses, props.shouldRestrictToCountry]);

  const addressesToSelect = React.useMemo(() => {
    return (
      <React.Fragment>
        <ul className="SavedAddressFormFragment">
          {addressesAvailableToSelect.map((savedAddress) => (
            <li key={`${props.addressFormType}--${savedAddress.id}`}>
              <SavedAddressItem
                addressFormType={props.addressFormType}
                value={savedAddress}
                currentlySelectedAddressId={currentlySelectedAddressId}
                handleSelectAddress={handleSelectAddress}
              />
            </li>
          ))}
        </ul>
      </React.Fragment>
    );
  }, [
    customerInformation,
    customerInformation?.addresses,
    currentlySelectedAddressId,
    handleSelectAddress,
    props.addressFormType,
    props.shouldRestrictToCountry,
    addressesAvailableToSelect,
  ]);

  return (
    <React.Fragment>
      {addressesToSelect}

      <div className="SavedAddressFormFragment-actions">
        <a
          className="or-add-a-new-address"
          onClick={props.handleEnterANewAddressInstead}
          href="javascript:void(0)"
        >{`Or add a new address`}</a>

        {props.formRef_isBillingAndShippingTheSame && (
          <CheckboxInput2023
            formRef={props.formRef_isBillingAndShippingTheSame}
            onChange={handleLastSelected_isBillingAndShippingTheSameChange}
            id={"isBillingAndShippingTheSame"}
          >{`My billing and shipping address are the same`}</CheckboxInput2023>
        )}
      </div>

      <ProminentErrorMessageBox
        area={DeliveryDetailsUserExperience_ProminentErrorAreas.DELETE_ADDRESS}
      />
    </React.Fragment>
  );
};
